import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { Column as RdgColumn } from "react-data-grid";

import { NotesBlueIcon } from "../../../../assets";
import { LocalStorageConstants, OrganisationTypeConstants } from "../../../../constants";
import { InteractionObjectType, InteractionType } from "../../../../constants/interaction";
import { CursorChangeProps, DataGridColumnCurrency, ResultType, SelectData } from "../../../../models";
import {
  getOrganisationDetails,
  SearchPipelineDealsRequest,
  SearchPipelineDealsResponse,
} from "../../../../service/query";
import { searchInteractions, searchPipelineDeals } from "../../../../service/query/QueryService.full";
import { FilterGroups, ResultData, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { flattenObject, jsonReviver, recursivelyValidateAndTransformDateToString } from "../../../../utils";
import { getDealListingRoute } from "../../../../utils/routes";
import {
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
  ProfileMenuItemType,
} from "../../../../widget";
import { AvatarCellFormatter } from "../../../../widget/data/DataGrid/components/CellFormatters";
import { EditColumns, IconCellFormatterData } from "../../../../widget/data/DataGrid/models";
import { createEditColumns } from "../../../../widget/data/DataGrid/utils/editColumns";

interface UsePortfolioTrackerReturnData {
  columns: DataGridColumnDefinition[];
  dropdownFilterKey: string | undefined;
  dropdownFiltersSelectData: SelectData;
  tagsFilterSelectData: SelectData;
  selectedTagKeys: string[];
  filtersRef: RefObject<HTMLDivElement>;
  filtersWidth: number;
  actionsMenuOptionsList: ProfileMenuItemType[];
  isLoading: boolean;
  refresh?: boolean;
  hasData: boolean;
  exportTable?: boolean;
  exportFileName: string;
  filterExportRows: (columns: ResultData[]) => ResultData[];
  filterExportColumns: (columns: RdgColumn<ResultData, unknown>[]) => RdgColumn<ResultData, unknown>[];

  portfolioTrackerLocalStorageItemName: string;
  defaultEditColumns: EditColumns;
  notesDealUuid?: string;

  showActionsMenu: boolean;
  setShowActionsMenu: Dispatch<SetStateAction<boolean>>;
  showEditColumnsPanel: boolean;
  setShowEditColumnsPanel: Dispatch<SetStateAction<boolean>>;
  showNotesPanel: boolean;
  closeNotesPanel: () => void;

  onDropdownFilter: (key: string | undefined) => Promise<void>;
  onTagsFilter: (keys: string[]) => Promise<void>;
  onClearFilters: () => void;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getColumns = (): (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] => [
  {
    name: "Deal",
    key: "dealLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  },
  {
    name: "Opportunity",
    key: "opportunity",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: false,
  },
  {
    name: "Location",
    key: "location",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: false,
  },
  {
    name: "Stage",
    key: "cachedStage",
    dataType: "string",
    formatter: "defaultPill",
    filterable: true,
    sortable: true,
  },
  // TODO-JWI: This doesn't map to any API field...
  {
    name: "Developer",
    key: "developer",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: false,
  },
  // NOTE: These don't map to any API field yet, to update with workflows and tasks extension
  // {
  //   name: "Next milestone",
  //   key: "nextMilestone",
  //   dataType: "string",
  //   formatter: "stringWithEllipsis",
  //   filterable: false,
  //   sortable: false,
  // },
  // {
  //   name: "Next deadline",
  //   key: "nextDeadline",
  //   dataType: "Date",
  //   formatter: "dateOnlyWithIndicator",
  //   filterable: false,
  //   sortable: false,
  // },
  {
    key: "avatar",
    name: "Lead",
    dataType: "string",
    formatter: "custom",
    alignment: "center",
    customCellFormatter: AvatarCellFormatter,
    filterable: true,
    sortable: true,
  },
  {
    name: "Deal type",
    key: "dealTypes",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: false,
  },
  {
    name: "Introduction date",
    key: "introductionDate",
    dataType: "Date",
    formatter: "dateOnly",
    filterable: false,
    sortable: true,
  },
  {
    name: "Target close date",
    key: "targetCloseDate",
    dataType: "Date",
    formatter: "dateOnly",
    filterable: false,
    sortable: true,
  },
  {
    name: "Investment value",
    key: "investmentValue",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    filterable: true,
    sortable: true,
  },
  {
    name: "Conversion probability",
    key: "conversionProbability",
    dataType: "number",
    formatter: "percentage",
    filterable: false,
    sortable: true,
  },
  {
    name: "Target returns",
    key: "targetReturns",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    filterable: true,
    sortable: true,
  },
  {
    name: "Estimated fees",
    key: "estimatedFees",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    filterable: true,
    sortable: true,
  },
  {
    name: "Source",
    key: "source",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Notes",
    key: "notesButton",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    sortable: false,
  },
];

const filterExportRows = (rows: ResultData[]): ResultData[] => {
  const rowsClone = recursivelyValidateAndTransformDateToString(JSON.parse(JSON.stringify(rows), jsonReviver))
    .data as ResultData[];
  rowsClone.forEach((row) => {
    // eslint-disable-next-line no-param-reassign
    if (row.notesButton !== undefined) delete row.notesButton;
    // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-explicit-any
    if (row.avatar !== undefined) row.avatar = (row.avatar as any).fullName || "";
  });
  return rowsClone;
};

const filterExportColumns = (rdgColumns: RdgColumn<ResultData, unknown>[]): RdgColumn<ResultData, unknown>[] => {
  return rdgColumns.filter((column) => column.key !== "notesButton");
};

enum DropdownFilterKeys {
  APPROVED_DEALS = "approvedDeals",
  REJECTED_DEALS = "rejectedDeals",
  YOUR_DEALS = "yourDeals",
}

const dropdownFiltersSelectData: SelectData = [
  { key: DropdownFilterKeys.APPROVED_DEALS, value: "Approved deals" },
  { key: DropdownFilterKeys.REJECTED_DEALS, value: "Rejected deals" },
  { key: DropdownFilterKeys.YOUR_DEALS, value: "Your deals" },
];

export const usePortfolioTracker = (): UsePortfolioTrackerReturnData => {
  const { currentOrganisationUuid, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(true);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dropdownFilterCriteria, setDropdownFilterCriteria] = useState<any>();
  const [dropdownFilterKey, setDropdownFilterKey] = useState<string>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [tagsFilterCriteria, setTagsFilterCriteria] = useState<any>();
  const [tagsFilterSelectData, setTagsFilterSelectData] = useState<SelectData>([]);
  const [selectedTagKeys, setSelectedTagKeys] = useState<string[]>([]);

  const filtersRef = useRef<HTMLDivElement>(null);
  const [filtersWidth, setFiltersWidth] = useState(420);
  useEffect(() => {
    const el = filtersRef.current;
    if (!el) return;

    const updateWidth = (): void => setFiltersWidth(el.getBoundingClientRect().width);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, []);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showEditColumnsPanel, setShowEditColumnsPanel] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);

  const [exportTable, setExportTable] = useState<boolean>();
  const [organisationDisplayName, setOrganisationDisplayName] = useState<string>();
  const [notesDealUuid, setNotesDealUuid] = useState<string>();

  const actionsMenuOptionsList: ProfileMenuItemType[] = [
    {
      id: 1,
      value: "Edit columns",
      action: () => setShowEditColumnsPanel(true),
    },
    {
      id: 2,
      value: "Export table",
      action: () => setExportTable(!exportTable),
    },
  ];

  const closeNotesPanel = (): void => {
    setNotesDealUuid(undefined);
    setShowNotesPanel(false);
  };

  const formatData = useCallback(
    (responseData: SearchPipelineDealsResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el) as ResultData & { avatar: { fullName?: string } };

        const [firstOpportunity, ...otherOpportunities] = el.relatedObjects;
        let opportunityLabel = "";
        if (firstOpportunity) {
          opportunityLabel =
            otherOpportunities.length > 0
              ? `${firstOpportunity.name} +${otherOpportunities.length}`
              : firstOpportunity.name;
        }
        result.opportunity = opportunityLabel;
        result.location = el.relatedObjects
          .filter((x) => x.location !== null)
          .map((x) => x.location)
          .join(" | ");

        result.developer = el.relatedObjects
          .filter((x) => x.developerName !== null)
          .map((x) => x.developerName)
          .join(" | ");

        result.dealTypes = el.types.map((type) => type.name).join(" | ");

        result.dealLink = {
          text: el.name,
          to: getDealListingRoute(el.uuid, OrganisationTypeConstants.ASSET_MANAGER),
        } as DataGridLinkCellFormatterData;

        result.notesButton = {
          action: () => {
            setNotesDealUuid(el.uuid);
            setShowNotesPanel(true);
          },
          icon: <NotesBlueIcon />,
        } as IconCellFormatterData;

        result.conversionProbability = el.conversionProbability !== null ? el.conversionProbability * 100 : null;

        result.avatar = { fullName: el.leadUser.fullName ?? undefined };

        return result;
      }) || [],
    []
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.dealLink) {
      filterCriteria.name = filterCriteria.dealLink;
      delete filterCriteria.dealLink;
    }

    if (filterCriteria.opportunity) {
      if (!filterCriteria.relatedObjects) {
        filterCriteria.relatedObjects = { some: {} };
      }
      filterCriteria.relatedObjects.some.name = filterCriteria.opportunity;
      delete filterCriteria.opportunity;
    }

    if (filterCriteria.location) {
      if (!filterCriteria.relatedObjects) {
        filterCriteria.relatedObjects = { some: {} };
      }
      filterCriteria.relatedObjects.some.location = filterCriteria.location;
      delete filterCriteria.location;
    }

    if (filterCriteria.developer) {
      if (!filterCriteria.relatedObjects) {
        filterCriteria.relatedObjects = { some: {} };
      }
      filterCriteria.relatedObjects.some.developerName = filterCriteria.developer;
      delete filterCriteria.developer;
    }

    if (filterCriteria.dealTypes) {
      filterCriteria.types = {
        some: {
          name: filterCriteria.dealTypes,
        },
      };
      delete filterCriteria.dealTypes;
    }

    if (filterCriteria.avatar) {
      filterCriteria.leadUser = {
        fullName: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }

    const filterGroups: FilterGroups<SearchPipelineDealsRequest["filter"]>["filterGroups"] = [];

    if (dropdownFilterCriteria && Object.keys(dropdownFilterCriteria).length > 0) {
      filterGroups.push({
        operator: "and",
        filter: {
          results: dropdownFilterCriteria,
        },
      });
    }

    if (tagsFilterCriteria && Object.keys(tagsFilterCriteria).length > 0) {
      filterGroups.push({
        operator: "and",
        filter: {
          results: tagsFilterCriteria,
        },
      });
    }

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const sortKeyMap: Record<string, string> = {
      dealLink: "name",
      avatar: "leadUser.fullName",
      conversionProbability: "conversionProbability",
    };

    if (currentOrganisationUuid) {
      setIsLoading(true);
      const request: SearchPipelineDealsRequest & FilterGroups<SearchPipelineDealsRequest["filter"]> = {
        paging: {
          beforeCursor: paging.beforeCursor || null,
          afterCursor: paging.afterCursor || null,
          limit: paging.pageSize,
        },
        /* eslint-disable @typescript-eslint/no-explicit-any */
        sort: sorting.map((s: { key: any; direction: any }) => ({
          key: sortKeyMap[s.key] || s.key,
          direction: s.direction,
        })),
        /* eslint-enable */
        organisationUuid: currentOrganisationUuid,
        filter: { results: filterCriteria },
        ...(filterGroups.length ? { filterGroups } : {}),
      };

      await searchPipelineDeals(request)
        .then((response) => {
          data = {
            resultData: formatData(response.data),
            paging: {
              startCursor: response.data?.paging?.startCursor || "",
              endCursor: response.data?.paging?.endCursor || "",
              pageSize: paging.pageSize,
              totalCount: response.data?.paging?.total || 0,
              hasNextPage: response.data?.paging?.hasNextPage || false,
              hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
            },
          };
        })
        .finally(() => {
          setHasData(data.resultData.length > 0);
          setIsLoading(false);
        });
    }

    return data;
  };

  const onDropdownFilter = async (key: string | undefined): Promise<void> => {
    const filterCriteria = key ? dataGridMapFilterCriteria([]) : undefined;

    switch (key) {
      case DropdownFilterKeys.APPROVED_DEALS:
        filterCriteria.status = {
          operator: "eq",
          value: "Approved",
        };
        break;
      case DropdownFilterKeys.REJECTED_DEALS:
        filterCriteria.status = {
          operator: "eq",
          value: "Rejected",
        };
        break;
      case DropdownFilterKeys.YOUR_DEALS:
        if (user?.userUuid) {
          filterCriteria.dealTeam = {
            some: {
              uuid: {
                operator: "eq",
                value: user.userUuid,
              },
            },
          };
        }
        break;
      default:
    }

    setDropdownFilterKey(key);
    setDropdownFilterCriteria(() => filterCriteria);
    setRefresh((prevRefresh) => !prevRefresh);
  };

  const onTagsFilter = async (keys: string[]): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    const searchInteractionsFilterCriteria = dataGridMapFilterCriteria([]);

    setSelectedTagKeys(keys);

    if (keys.length > 0) {
      searchInteractionsFilterCriteria.uuid = {
        operator: "in",
        value: keys,
      };

      await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 50,
        },
        filter: { results: searchInteractionsFilterCriteria },
      }).then((response) => {
        if (response.data?.results != null && response.data.results.length > 0) {
          filterCriteria.uuid = {
            operator: "in",
            value: response.data.results
              .filter((interaction) => interaction.objectInteractions.length > 0)
              .map((interaction) =>
                interaction.objectInteractions
                  .filter(
                    (objectInteraction) =>
                      objectInteraction.objectType === InteractionObjectType.DEAL && objectInteraction.endDate == null
                  )
                  .map((objectInteraction) => objectInteraction.objectUuid)
              )
              .flat(),
          };
        }
      });
    }

    setTagsFilterCriteria(() => filterCriteria);
    setRefresh((prevRefresh) => !prevRefresh);
  };

  const onClearFilters = (): void => {
    setDropdownFilterKey(undefined);
    setDropdownFilterCriteria(undefined);
    setSelectedTagKeys([]);
    setTagsFilterCriteria(undefined);
    setRefresh((prevRefresh) => !prevRefresh); // single toggle -> effect fires
  };

  const getOrganisationTags = useCallback(async (organisationUuid: string): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.type = {
      operator: "eq",
      value: InteractionType.TAG,
    };
    filterCriteria.objectType = {
      operator: "eq",
      value: InteractionObjectType.ORGANISATION,
    };
    filterCriteria.objectUuid = {
      operator: "eq",
      value: organisationUuid,
    };

    await searchInteractions({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      filter: { results: filterCriteria },
    }).then((response) => {
      if (response.data?.results != null && response.data.results.length > 0) {
        setTagsFilterSelectData(
          response.data.results.map((interaction) => ({
            key: interaction.uuid,
            value: JSON.parse(interaction.data, jsonReviver).name,
          })) as SelectData
        );
      }
    });
  }, []);

  const getOrganisationDisplayName = useCallback(
    async (organisationUuid: string) => {
      await getOrganisationDetails({ organisationUuid }).then((res) => {
        if (res.status === Status.Success && res.data) {
          setOrganisationDisplayName(res.data.displayName ?? undefined);
        }
      });
    },
    [currentOrganisationUuid]
  );

  useEffect(() => {
    if (currentOrganisationUuid) getOrganisationDisplayName(currentOrganisationUuid);
  }, [currentOrganisationUuid]);

  useEffect(() => {
    if (currentOrganisationUuid) getOrganisationTags(currentOrganisationUuid);
  }, [currentOrganisationUuid]);

  return {
    columns: getColumns(),
    isLoading,
    dropdownFilterKey,
    dropdownFiltersSelectData,
    tagsFilterSelectData,
    selectedTagKeys,
    filtersRef,
    filtersWidth,
    refresh,
    hasData,
    actionsMenuOptionsList,
    exportTable,
    exportFileName: `${organisationDisplayName ? `${organisationDisplayName}_` : ""}Pipeline_tracker_`.replaceAll(
      " ",
      "_"
    ),
    filterExportRows,
    filterExportColumns,
    portfolioTrackerLocalStorageItemName: `${user?.userUuid ? `${user?.userUuid}_` : ""}${LocalStorageConstants.PIPELINE_TRACKER_PREFERENCES}`,
    defaultEditColumns: createEditColumns({
      dealLink: { show: true },
      opportunity: { show: true },
      location: { show: true },
      cachedStage: { show: true },
      developer: { show: true },
      // nextMilestone: { show: true },
      // nextDeadline: { show: true },
      avatar: { show: true },
      dealTypes: { show: true },
      deadline: { show: true },
      introductionDate: { show: true },
      targetCloseDate: { show: true },
      investmentValue: { show: true },
      conversionProbability: { show: true },
      targetReturns: { show: true },
      estimatedFees: { show: true },
      source: { show: true },
      notesButton: { show: true },
    }),

    notesDealUuid,
    showActionsMenu,
    setShowActionsMenu,
    showEditColumnsPanel,
    setShowEditColumnsPanel,
    showNotesPanel,
    closeNotesPanel,

    onDropdownFilter,
    onTagsFilter,
    onClearFilters,
    onChange,
  };
};
